import { useQuery } from '@tanstack/react-query';
import type { SynchronousQueryConfig } from 'lib/react-query';
import queryString from 'query-string';
import { apiClient } from 'lib/api';
import queryKeys from './queryKeys';
import type { CreativeEntry } from 'types/shared';
import type { UploadDate } from './getCreatives';

export interface CreativesWithHypothesisParams {
  app?: string;
  ad_type?: string;
  network?: string;
  hypothesis?: string[];
  customer?: string;
  flow?: string[];
  technology?: string[];
  status?: string;
  query?: string;
  tpc_finish_date?: string;
  uploaded?: UploadDate;
}

export enum HypothesisKey {
  NoHypothesis = 'No hypothesis',
}

export type CreativesByHypothesis<T extends CreativeEntry = CreativeEntry> = Record<
  string,
  T[]
>;

const toRequestParams = ({
  app,
  uploaded,
  ...filters
}: CreativesWithHypothesisParams): Record<string, unknown> => {
  const activeFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) =>
      Array.isArray(value) ? value.length > 0 : !!value,
    ),
  );

  return {
    app,
    ...activeFilters,
    ...(uploaded && uploaded !== 'all' ? { upload_date: uploaded } : {}),
  };
};

const withRenderableHypotheses = <T extends CreativeEntry>(
  byHypothesis: CreativesByHypothesis<T>,
): CreativesByHypothesis<T> =>
  Object.fromEntries(
    Object.entries(byHypothesis).filter(
      ([hypothesis, creatives]) => !!hypothesis.trim() && creatives.length > 0,
    ),
  );

export const getCreativesWithHypothesis = async <
  T extends CreativeEntry = CreativeEntry,
>(
  { params }: { params: CreativesWithHypothesisParams },
  signal?: AbortSignal,
): Promise<CreativesByHypothesis<T>> => {
  const res = await apiClient.get<CreativesByHypothesis<T>>(
    (queryKeys.allCreativesWithHypothesis as string[]).join('/'),
    {
      params: toRequestParams(params),
      signal,
      paramsSerializer: (prms) =>
        queryString.stringify(prms, { arrayFormat: 'comma' }),
    },
  );
  return withRenderableHypotheses(res.data);
};

interface UseCreativesWithHypothesisOptions<T extends CreativeEntry> {
  params: CreativesWithHypothesisParams;
  config?: SynchronousQueryConfig<
    typeof getCreativesWithHypothesis<T>,
    CreativesByHypothesis<T>
  >;
}

export const useCreativesWithHypothesis = <T extends CreativeEntry = CreativeEntry>({
  params,
  config,
}: UseCreativesWithHypothesisOptions<T>) =>
  useQuery({
    staleTime: 0,
    refetchOnMount: true,
    ...config,
    queryFn: ({ signal }) => getCreativesWithHypothesis<T>({ params }, signal),
    queryKey: queryKeys.creativesWithHypothesis<CreativesWithHypothesisParams>(params),
  });
